"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import ProgramHero from "../src/components/mentorship/Hero";
import StatsCardsWrapper from "../src/components/mentorship/Stats";
import ProgramOutcomesSection from "../src/components/mentorship/ProgramOutcomesSection";
import SuccessStories from "../src/components/home/SuccessStories";
import CompaniesSection from "../src/components/home/CompaniesSection";
import TestimonialsSection from "../src/components/home/TestimonialsSection";
import Business from "../src/components/home/Business";
import StickyBanner from "../src/components/mentorship/Sticky";
import programsDataJson from "../src/data/mentorship-programs.json";
import mentorsDataJson from "../src/data/mentors.json";

export default function ProgramDetailPage({
  initialProgram,
  initialMentors = [],
  allProgramsInitial = [],
}) {
  const router = useRouter();
  const { slug } = router.query;

  const [program, setProgram] = useState(initialProgram || null);
  const [mentors, setMentors] = useState(initialMentors || []);
  const [allPrograms, setAllPrograms] = useState(allProgramsInitial || []);
  const [loading] = useState(false);
  const [error] = useState(null);
  const [notFound] = useState(!initialProgram);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002726] mx-auto mb-4"></div>
          <p className="text-[#002726] text-lg">Loading program...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">
            Error loading program: {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#002726] text-white px-4 py-2 rounded-lg hover:bg-[#004726] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#002726] mb-4">
            Program Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The program you&apos;re looking for doesn&apos;t exist.
          </p>
          <button
            onClick={() => router.push("/")}
            className="bg-[#002726] text-white px-4 py-2 rounded-lg hover:bg-[#004726] transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>{program.heading} | GrowthSchool</title>
        <meta name="description" content={program.subheading} />
      </Head>

      <div>
        <ProgramHero program={program} mentors={mentors} />
        <StatsCardsWrapper />
        <ProgramOutcomesSection mentors={mentors} />
        <SuccessStories />
        <CompaniesSection />
        <TestimonialsSection />
        <Business />
        <StickyBanner programs={allPrograms} />
      </div>
    </>
  );
}

export async function getStaticPaths() {
  const programs = require("../src/data/mentorship-programs.json");
  const paths = (programs || []).map((p) => ({ params: { slug: p.slug } }));
  return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
  const programs = require("../src/data/mentorship-programs.json");
  const mentors = require("../src/data/mentors.json");
  const program = (programs || []).find((p) => p.slug === params.slug) || null;

  return {
    props: {
      initialProgram: program,
      initialMentors: mentors || [],
      allProgramsInitial: programs || [],
    },
  };
}
